import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CommunityComment, CommunityPost, CommunityTopic } from '../types';
import type { AsyncStatus } from '../redux/communitySlice';
import CommunityCommentList from './CommunityCommentList';
import { API_BASE_URL } from '../services/apiClient';
import './CommunityPostCard.css';

interface CommunityPostCardProps {
  post: CommunityPost;
  topicsDict: Record<string, CommunityTopic>;
  comments: CommunityComment[];
  commentsStatus: AsyncStatus;
  commentsError: string | null;
  createStatus: AsyncStatus;
  createError: string | null;
  onLoadComments: () => void;
  onSubmitComment: (content: string, parentCommentId?: string | null) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
  currentUserId?: number | null;
  onDeletePost?: () => Promise<void>;
  onToggleLike?: () => Promise<void> | void;
  likePending?: boolean;
  onToggleFollow?: () => Promise<void> | void;
  followPending?: boolean;
  defaultExpanded?: boolean;
  hasSaved?: boolean;
  onToggleSave?: () => Promise<void> | void;
  savePending?: boolean;
  onOpenPost?: () => void;
  onOpenAuthor?: () => void;
  onToggleCommentLike: (commentId: string, hasLiked: boolean) => Promise<void>;
  commentLikePending?: Record<string, boolean>;
  onOpenRepost?: (postId: string) => void;
}

const HASHTAG_PATTERN = /(#[A-Za-z0-9_\-\u4e00-\u9fa5]+)/g;

const highlightHashtags = (text: string, onClickTopic?: (topic: string) => void) => {
  const segments = text.split(HASHTAG_PATTERN);
  if (segments.length === 1) {
    return text;
  }
  return segments.map((segment, index) => {
    if (!segment.startsWith('#')) {
      return <span key={index}>{segment}</span>;
    }
    const topicId = segment.slice(1);
    if (!onClickTopic) {
      return (
        <span key={index} className="community-post-card__hashtag community-post-card__hashtag--static">
          {segment}
        </span>
      );
    }
    const handleClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      onClickTopic(topicId);
    };
    return (
      <button
        key={index}
        type="button"
        className="community-post-card__hashtag"
        onClick={handleClick}
      >
        {segment}
      </button>
    );
  });
};

const buildMediaUrl = (url: string | null | undefined): string => {
  if (!url) {
    return '';
  }
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  const base = API_BASE_URL.replace(/\/api\/?$/, '');
  const normalized = url.startsWith('/') ? url : `/${url}`;
  return `${base}${normalized}`;
};

const CommunityPostCard: React.FC<CommunityPostCardProps> = ({
  post,
  topicsDict,
  comments,
  commentsStatus,
  commentsError,
  createStatus,
  createError,
  onLoadComments,
  onSubmitComment,
  onDeleteComment,
  currentUserId,
  onDeletePost,
  onToggleLike,
  likePending,
  onToggleFollow,
  followPending,
  defaultExpanded = false,
  hasSaved = false,
  onToggleSave,
  savePending,
  onOpenPost,
  onOpenAuthor,
  onToggleCommentLike,
  commentLikePending = {},
  onOpenRepost,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  useEffect(() => {
    if (expanded && commentsStatus === 'idle') {
      onLoadComments();
    }
  }, [expanded, commentsStatus, onLoadComments]);

  const topicLabels = useMemo(() => {
    if (post.topic_details?.length) {
      return post.topic_details;
    }
    return post.topics
      .map((id) => topicsDict[id])
      .filter((topic): topic is CommunityTopic => Boolean(topic));
  }, [post.topic_details, post.topics, topicsDict]);

  const handleToggleComments = () => {
    setExpanded((prev) => !prev);
  };

  const handlePrescriptionNavigate = () => {
    if (!post.prescription) {
      return;
    }
    const targetIdentifier = post.prescription.code || (post.prescription.id ? String(post.prescription.id) : '');
    if (targetIdentifier) {
  navigate(`/app/prescriptions/${targetIdentifier}`);
    }
  };

  const handleDeletePost = async () => {
    if (!onDeletePost) {
      return;
    }
    const confirmed = window.confirm('确认删除该帖子吗？删除后无法恢复');
    if (!confirmed) {
      return;
    }
    try {
      setIsDeleting(true);
      await onDeletePost();
    } catch (error) {
      const message = error instanceof Error ? error.message : '删除失败，请稍后重试';
      window.alert(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const hasMedia = post.media && post.media.length > 0;
  const viewer = post.viewer;
  const isAuthor = currentUserId != null && post.author?.id === currentUserId;

  const handleLikeClick = () => {
    if (!onToggleLike || likePending) {
      return;
    }
    void onToggleLike();
  };

  const handleFollowClick = () => {
    if (!onToggleFollow || followPending) {
      return;
    }
    void onToggleFollow();
  };

  const handleSaveClick = () => {
    if (!onToggleSave || savePending) {
      return;
    }
    void onToggleSave();
  };

  const handleAuthorClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (!onOpenAuthor || !post.author?.id) {
      return;
    }
    event.stopPropagation();
    onOpenAuthor();
  };

  const handleAuthorKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleAuthorClick(event);
    }
  };

  const handleContentClick = (event: React.MouseEvent) => {
    if (!onOpenPost) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target.closest('button, a, input, textarea')) {
      return;
    }
    onOpenPost();
  };

  const handleContentKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onOpenPost) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      const target = event.target as HTMLElement;
      if (target.closest('button, a, input, textarea')) {
        return;
      }
      event.preventDefault();
      onOpenPost();
    }
  };

  return (
    <article className={`community-post-card${onOpenPost ? ' community-post-card--interactive' : ''}`}>
      <header className="community-post-card__header">
        <div
          className={`community-post-card__author${onOpenAuthor && post.author?.id ? ' community-post-card__author--clickable' : ''}`}
          role={onOpenAuthor && post.author?.id ? 'button' : undefined}
          tabIndex={onOpenAuthor && post.author?.id ? 0 : undefined}
          onClick={handleAuthorClick}
          onKeyDown={handleAuthorKeyDown}
        >
          <div className="community-post-card__avatar">
            {post.author?.avatar_url ? (
              <img src={post.author.avatar_url} alt={post.author?.name || post.author?.username || '作者'} />
            ) : (
              <span>{(post.author?.name || post.author?.username || '友').slice(0, 1).toUpperCase()}</span>
            )}
          </div>
          <div className="community-post-card__author-info">
            <span className="community-post-card__author-name">{post.author?.name || post.author?.username || '匿名用户'}</span>
            <span className="community-post-card__timestamp">
              {post.created_at ? new Date(post.created_at).toLocaleString() : '刚刚'}
            </span>
          </div>
        </div>
        <div className="community-post-card__header-actions">
          {onToggleFollow && !isAuthor && (
            <button
              type="button"
              className={`community-post-card__follow${viewer?.follows_author ? ' community-post-card__follow--active' : ''}`}
              onClick={handleFollowClick}
              disabled={followPending}
            >
              {followPending ? '处理中...' : viewer?.follows_author ? '已关注' : '关注'}
            </button>
          )}
          {post.permissions?.can_edit && (
            <button
              type="button"
              className="community-post-card__delete"
              onClick={handleDeletePost}
              disabled={isDeleting}
            >
              {isDeleting ? '删除中...' : '删除'}
            </button>
          )}
        </div>
      </header>

      <div
        className={`community-post-card__content${onOpenPost ? ' community-post-card__content--interactive' : ''}`}
        onClick={handleContentClick}
        onKeyDown={handleContentKeyDown}
        role={onOpenPost ? 'button' : undefined}
        tabIndex={onOpenPost ? 0 : undefined}
      >
        <h3 className="community-post-card__title">{post.title}</h3>
        <div className="community-post-card__body">
          {post.content.split('\n').map((line, index) => (
            <p key={index}>{highlightHashtags(line)}</p>
          ))}
        </div>
      </div>

      {post.repost_of && (
        <div className="community-post-card__repost">
          <button
            type="button"
            className="community-post-card__repost-button"
            onClick={(event) => {
              event.stopPropagation();
              if (onOpenRepost) {
                onOpenRepost(post.repost_of!.post_id);
              }
            }}
          >
            <div className="community-post-card__repost-author">
              {post.repost_of.author?.avatar_url ? (
                <img src={post.repost_of.author.avatar_url} alt={post.repost_of.author.name ?? post.repost_of.author.username ?? '原帖作者'} />
              ) : (
                <span>{(post.repost_of.author?.name || post.repost_of.author?.username || '友').slice(0, 1).toUpperCase()}</span>
              )}
              <span>{post.repost_of.author?.name || post.repost_of.author?.username || '社区用户'}</span>
            </div>
            <div className="community-post-card__repost-body">
              <strong>{post.repost_of.title || '转发内容'}</strong>
              {post.repost_of.excerpt && <p>{post.repost_of.excerpt}</p>}
            </div>
          </button>
        </div>
      )}

      {topicLabels.length > 0 && (
        <div className="community-post-card__topics">
          {topicLabels.map((topic) => (
            <span key={topic.id} className="community-post-card__topic-chip">{topic.label}</span>
          ))}
        </div>
      )}

      {hasMedia && (
        <div className="community-post-card__media">
          {post.media.map((item) => {
            const mediaUrl = buildMediaUrl(item.url);
            if (!mediaUrl) {
              return null;
            }
            return (
              <div key={mediaUrl} className="community-post-card__media-item">
                {item.kind === 'image' ? (
                  <img src={mediaUrl} alt={item.name || post.title} loading="lazy" />
                ) : (
                  <video
                    controls
                    src={mediaUrl}
                    poster={item.cover_url ? buildMediaUrl(item.cover_url) : undefined}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {post.prescription && (
        <div className="community-post-card__prescription">
          <span>关联处方：</span>
          <button type="button" onClick={handlePrescriptionNavigate}>
            {post.prescription.title || post.prescription.code || `处方 #${post.prescription.id}`}
          </button>
        </div>
      )}

      <footer className="community-post-card__footer">
        <div className="community-post-card__meta">
          {onToggleLike && (
            <button
              type="button"
              className={`community-post-card__like${viewer?.has_liked ? ' community-post-card__like--active' : ''}`}
              onClick={handleLikeClick}
              disabled={likePending}
            >
              {likePending ? '加载中...' : `赞同 ${post.stats?.likes ?? 0}`}
            </button>
          )}
          {onToggleSave && (
            <button
              type="button"
              className={`community-post-card__save${hasSaved ? ' community-post-card__save--active' : ''}`}
              onClick={handleSaveClick}
              disabled={Boolean(savePending)}
              aria-pressed={hasSaved}
            >
              {savePending ? '处理中...' : hasSaved ? '已收藏' : '收藏'}
            </button>
          )}
          <span className="community-post-card__stats">评论 {post.stats?.comments ?? 0}</span>
        </div>
        <button type="button" className="community-post-card__comments-toggle" onClick={handleToggleComments}>
          {expanded ? '收起评论' : '查看评论'}
        </button>
      </footer>

      {expanded && (
        <CommunityCommentList
          comments={comments}
          status={commentsStatus}
          error={commentsError}
          onReload={onLoadComments}
          onSubmit={onSubmitComment}
          onDelete={onDeleteComment}
          onToggleLike={onToggleCommentLike}
          likePending={commentLikePending}
          createStatus={createStatus}
          createError={createError}
          currentUserId={currentUserId}
          postOwnerId={post.author?.id}
        />
      )}
    </article>
  );
};

export default CommunityPostCard;
